'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import type { SessionMembership } from '@/types/next-auth';

interface OrganizationContextValue {
  /** Organization id read from the URL's [orgId] segment (validation-only, not authoritative). */
  orgId: string | null;
  /** The session's active organization membership — the actual data-scoping source. */
  activeOrganization: SessionMembership | null;
  memberships: SessionMembership[];
  /** True in the brief window where the URL orgId and session's active org differ (mid-switch). */
  isMismatched: boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const params = useParams();
  const { data: session } = useSession();

  const value = useMemo<OrganizationContextValue>(() => {
    const orgId = typeof params?.orgId === 'string' ? params.orgId : null;
    const memberships = session?.user?.memberships ?? [];
    const activeOrganizationId = session?.user?.activeOrganizationId ?? null;
    const activeOrganization = memberships.find((m) => m.organizationId === activeOrganizationId) ?? null;

    return {
      orgId,
      activeOrganization,
      memberships,
      isMismatched: !!orgId && !!activeOrganizationId && orgId !== activeOrganizationId,
    };
  }, [params?.orgId, session?.user?.memberships, session?.user?.activeOrganizationId]);

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

const FALLBACK_VALUE: OrganizationContextValue = {
  orgId: null,
  activeOrganization: null,
  memberships: [],
  isMismatched: false,
};

/**
 * Reads org context. The provider is mounted at the app root (app/layout.tsx),
 * so this always has real data in the running app; the fallback below only
 * matters for unit tests that render a component in isolation without it.
 */
export function useOrganization(): OrganizationContextValue {
  const ctx = useContext(OrganizationContext);
  return ctx ?? FALLBACK_VALUE;
}
